import React from 'react';
import {
    Badge,
    TabContent,
    TabPane,
    Nav,
    NavItem,
    NavLink,
    Progress,
} from 'reactstrap';
import moment from 'moment';
import { renderToString } from 'react-dom/server';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarDay,
    faHistory,
    faCircleNotch,
    faMapMarkedAlt,
} from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { Link } from '@s20/components/common/Link';
import { crewLeadLabel } from '@s20/i18n';
import { getMapUrl } from '@s20/common/util/dom';
import { httpClient } from '@s20/services/httpClient';
import {
    DataGrid,
    TextFilterDefaults,
    LinkCellRenderer,
    ButtonCellRenderer,
    createDataSource,
    DateFilterDefaults,
    getMappedFilterModel,
    ServerSideScrollRowsRequest,
    indexCellRenderer,
    DateRangeReadOnlyFloatingFilterComponent,
    handleCellCopy,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, {
    ApiRoutes,
    ApplicationPermissions,
    AppNavPaths,

} from '../Common';
import {
    FlexColumnStart, FlexStartRow, PageWrap, SmallButton,
} from '../common/forms/FormElements';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import authService from '../api-authorization/AuthorizeService';
import { Alert } from '../common/forms/ModalForm';
import { IconHeader } from './IconHeader';
import { JobMapView } from './JobMapView';

const gsfFilterParams = {
    suppressFilterButton: true,
    options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ],
    optionsLabel: 'label',
    optionsValue: 'value',
    disableFilter: false,
    initialFilterValue: '',
};

const validateHistoryParameters = (filter_model) => {
    const validationMessages = [];

    // Date Range
    if (
        !filter_model?.AssignmentDate?.dateFrom
        || !filter_model?.AssignmentDate?.dateTo
    ) {
        validationMessages.push('[Date From] and [Date To] are required.');
    } else {
        const mFrom = moment(filter_model.AssignmentDate.dateFrom);
        const mTo = moment(filter_model.AssignmentDate.dateTo);

        if (!mTo.isSameOrAfter(mFrom)) {
            validationMessages.push(
                `Invalid date range: ${mFrom.format(
                    'M/D/YY',
                )} - ${mTo.format('M/D/YY')}`,
            );
        }
    }
    return validationMessages.length
        ? validationMessages.join('\r\n')
        : '';
};

const validateParameters = (filter_model) => {
    const validationMessages = [];

    // Date Range
    if (
        !filter_model?.AssignmentDate?.dateFrom
        || !filter_model?.AssignmentDate?.dateTo
    ) {
        validationMessages.push('[Date From] and [Date To] are required.');
    } else {
        const mFrom = moment(filter_model.AssignmentDate.dateFrom);
        const mTo = moment(filter_model.AssignmentDate.dateTo);

        if (!mTo.isSameOrAfter(mFrom)) {
            validationMessages.push(
                `Invalid date range: ${mFrom.format(
                    'M/D/YY',
                )} - ${mTo.format('M/D/YY')}`,
            );
        }

        if (mTo.diff(mFrom, 'days') > 14) {
            validationMessages.push(
                'Invalid date range: Date range cannot exceed 2 weeks.',
            );
        }
    }
    return validationMessages.length
        ? validationMessages.join('\r\n')
        : '';
};

class DailyJobView extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        // window.addEventListener('resize', this.onWindowResized);

        const viewportWidth = window.innerWidth;

        this.state = {
            loading: true,
            loadingData: false,
            rowData: [],
            historyRowData: [],
            selectedRow: null,
            isMobile: viewportWidth <= 767,
            activeTab: '1',
            dispatchLocations: [],
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onExportHistoryToExcelClicked = async () => {
        const { historyGridOptions } = { ...this.state };
        const filterModel = historyGridOptions.api.getFilterModel();
        const sortModel = historyGridOptions.api.columnModel.getColumnState();
        const filterModelMapped = getMappedFilterModel(
            { ...filterModel },
            { ...historyGridOptions },
        );
        const req = new ServerSideScrollRowsRequest();
        req.startRow = 0;
        req.endRow = 9999999;
        req.filterModel = { ...filterModelMapped };
        req.sortModel = Object.keys(sortModel).length ? sortModel : [];
        req.sortModel.shift();
        const fileName = `JobDailyViewHistory_${moment().format(
            'yyyyMMdd_hhmmss',
        )}.xlsx`;

        this.setState({
            isHistoryExporting: true,
        });

        await httpClient.downloadFile(
            ApiRoutes.job.dailyViewHistoryExcelExport(),
            req,
            fileName,
        );

        this.setState({
            isHistoryExporting: false,
        });
    };

    onExportToExcelClicked = async () => {
        const { gridOptions } = { ...this.state };
        const filterModel = gridOptions.api.getFilterModel();
        const sortModel = gridOptions.api.columnModel.getColumnState();
        const filterModelMapped = getMappedFilterModel(
            { ...filterModel },
            { ...gridOptions },
        );
        const req = new ServerSideScrollRowsRequest();
        req.startRow = 0;
        req.endRow = 9999999;
        req.filterModel = { ...filterModelMapped };
        req.sortModel = Object.keys(sortModel).length ? sortModel : [];
        req.sortModel.shift();
        const fileName = `JobDailyView_${moment().format(
            'yyyyMMdd_hhmmss',
        )}.xlsx`;
        await httpClient.downloadFile(
            ApiRoutes.job.dailyViewExcelExport(),
            req,
            fileName,
        );
    };

    getGridOptions = async (isMobile) => {
        const crewLead = crewLeadLabel(this.context.tenant?.tenantSettings);

        const hasJobAccess = this.context.checkUserHasPermission(ApplicationPermissions.job_create)
            || this.context.checkUserHasPermission(ApplicationPermissions.job_edit)
            || this.context.checkUserHasPermission(ApplicationPermissions.job_view);
        // for now default to false
        isMobile = false;

        const [dispatchLocations, jobTypes] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.jobTypes()),
        ]);

        // Admins can select all dispatch, others default to their dispatch loc.
        let initialDispatchValue = null;
        if (!this.context.user.isAdmin) initialDispatchValue = this.context.user.dispatchCompanyId;

        this.setState({
            dispatchLocations,
        });

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
            disableFilter: false,
            initialFilterValue: initialDispatchValue,
        };

        const jobTypeFilterParams = {
            suppressFilterButton: true,
            options: jobTypes,
            optionsLabel: 'label',
            optionsValue: 'label',
            disableFilter: false,
            initialFilterValue: '',
        };

        const context = this;
        const opts = {
            domLayout: 'normal',
            components: {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                linkRenderer: LinkCellRenderer,
                buttonRenderer: ButtonCellRenderer,
                nameRenderer: LinkCellRenderer,
            },
            suppressMenuHide: true,
            headerHeight: isMobile ? 0 : 34,
            rowHeight: isMobile ? 76 : 40,
            suppressPropertyNamesCheck: true,
            rowModelType: 'infinite',
            cacheBlockSize: 18, // with including subcontractors, row height increases. less rows are visible
            defaultColDef: {
                sortable: !isMobile,
                resizable: !isMobile,
                suppressMenu: true,
                floatingFilter: !isMobile,
            },
            filter: !isMobile,
            unSortIcon: true,
            rowSelection: 'single',
            multiSortKey: 'ctrl',
            onSelectionChanged: (event) => {
                const selection = event.api.getSelectedRows();
                context.setState({
                    selectedRow: selection.length ? selection[0] : null,
                });
            },
            refresh: () => {
                context.state.gridApi.setDatasource(context.state.dataSource);
            },
            setStatus: (status) => {
                context.setState({ gridStatus: status });
            },
            columnDefs: [
                {
                    flex: 0,
                    width: 70,
                    minWidth: 70,
                    maxWidth: 70,
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    cellRenderer: indexCellRenderer,
                },
                {
                    colId: 'Customer',
                    minWidth: 220,
                    flex: 1,
                    headerName: 'Customer',
                    field: 'customer',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: (params) => {
                        if (!params?.node?.data) return null;

                        if (params?.node?.data?.subcontractor) {
                            return (
                                <span>
                                    {params.node.data.customer}
                                    <br />
                                    <small className="text-muted">
                                        {params.node.data.subcontractor}
                                    </small>
                                </span>
                            );
                        }

                        return params.node.data.customer;
                    },
                },
                {
                    colId: 'CrewLead',
                    flex: 1,
                    minWidth: 220,
                    headerName: crewLead,
                    headerClass: 'jobview-crewlead',
                    field: 'crewLead',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: (params) => {
                        if (!params?.node?.data) return null;

                        if (params?.node?.data?.crewLead) return params.node.data.crewLead;
                        return <span> - </span>;
                    },
                },
                {
                    colId: 'Foreman',
                    flex: 1,
                    minWidth: 220,
                    headerName: 'Customer Foreman',
                    field: 'foreman',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'DispatchCompanyId',
                    headerName: 'Dispatching',
                    sortable: true,
                    minWidth: 220,
                    flex: 1,
                    field: 'dispatchLocation',
                    tooltipField: 'dispatchLocation',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...dispatchFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...dispatchFilterParams },
                },
                {
                    colId: 'JobNumber',
                    minWidth: 180,
                    flex: 1,
                    headerName: 'Job Number',
                    field: 'jobNumber',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: hasJobAccess
                        ? (params) => {
                            if (!params.data) return null;

                            const { projectId, jobId, jobNumber } = params.data;

                            const to = projectId
                                ? `${AppNavPaths.Project}/${projectId}?openJob=${jobId}`
                                : `${AppNavPaths.Job}/${jobId}`;

                            return (
                                <Link
                                    className="site-link"
                                    to={to}
                                    target="_blank"
                                >
                                    {jobNumber}
                                </Link>
                            );
                        }
                        : null,
                },
                {
                    colId: 'JobLocation',
                    minWidth: 220,
                    flex: 1,
                    headerName: 'Job Location',
                    field: 'jobLocation',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: LinkCellRenderer,
                    cellRendererParams: {
                        href: getMapUrl,
                        nameField: 'jobLocation',
                        idField: 'jobLocationAddress',
                        title: 'View this Job Location',
                    },
                },
                {
                    colId: 'WorkOrderNumber',
                    minWidth: 150,
                    flex: 1,
                    headerName: 'WO Number',
                    field: 'workOrderNumber',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    width: 220,
                    minWidth: 220,
                    colId: 'AssignmentDate',
                    headerName: 'Date',
                    field: 'assignmentDate',
                    sortable: true,
                    filter: 'agDateColumnFilter',
                    filterParams: {
                        ...DateFilterDefaults,
                        inRangeInclusive: true,
                        browserDatePicker: true,
                        suppressFilterButton: true,
                    },
                    floatingFilter: true,
                    floatingFilterComponent:
                        DateRangeReadOnlyFloatingFilterComponent,
                    floatingFilterComponentParams: {},
                },
                {
                    width: 100,
                    minWidth: 100,
                    colId: 'StartTime',
                    headerName: 'Start',
                    headerClass: 'text-center',
                    field: 'startTime',
                    sortable: true,
                },
                {
                    headerName: 'Required',
                    width: 120,
                    minWidth: 120,
                    field: 'requiredResources',
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                },
                {
                    headerName: 'Assignments',
                    width: 120,
                    minWidth: 120,
                    field: 'assignmentCount',
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                },
                {
                    headerName: 'Unfilled',
                    width: 120,
                    minWidth: 120,
                    field: 'unfilledCount',
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                },
                {
                    colId: 'JobType',
                    minWidth: 180,
                    flex: 1,
                    headerName: 'Job Type',
                    field: 'jobType',
                    sortable: true,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...jobTypeFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...jobTypeFilterParams },
                },
                {
                    colId: 'HasGSF',
                    headerName: 'GSF',
                    width: 120,
                    minWidth: 120,
                    field: 'hasGSF',
                    sortable: true,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...gsfFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...gsfFilterParams },
                },
                {
                    width: 70,
                    minWidth: 70,
                    headerName: '',
                    headerClass: 'text-center',
                    field: 'completedTimesheets',
                    headerComponent: IconHeader,
                    headerComponentParams: {
                        icon: faClock,
                    },
                    sortable: false,
                    cellRenderer: (params) => {
                        if (!params.data) return '';
                        const completed = params.data?.completedTimesheets ?? 0;
                        const count = params.data?.timesheetsCount ?? 0;
                        const done = completed > 0 && completed === count;

                        const display = count > 0 ? completed / count : 0;

                        return (
                            <div className="w-100 text-right">
                                <Badge
                                    color={done ? 'success' : 'secondary'}
                                    style={{ fontSize: '1em' }}
                                >
                                    {display}
                                </Badge>
                            </div>
                        );
                    },
                    cellStyle: { textAlign: 'center' },
                },
            ],
        };

        opts.onGridReady = (params) => {
            // default date filter values
            const today = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');

            const dateRangeFilterComponent = params.api.getFilterInstance('AssignmentDate');

            dateRangeFilterComponent.setModel({
                filterType: 'date',
                type: 'inRange',
                dateFrom: today,
                dateTo: today,
            });

            params.api.onFilterChanged();
            opts.api = params.api;
            params.api.setDatasource(context.state.dataSource, opts);
        };

        opts.onCellKeyDown = handleCellCopy;

        opts.setLoading = (b) => {
            context.setState({ loadingData: b });
        };

        opts.showLoadingOverlay = false;
        opts.overlayNoRowsTemplate = 'No job information found for the selected filters';

        // debug only
        // opts.successCallback = (data) => context.setState({ rowData: data });

        if (isMobile) {
            opts.isFullWidthCell = () => true;
            opts.fullWidthCellRenderer = context.fullWidthCellRenderer;
        } else {
            opts.isFullWidthCell = () => false;
            opts.fullWidthCellRenderer = null;
        }

        opts.defaultColDef = {
            ...opts.defaultColDef,
            cellStyle: {
                lineHeight: '18px',
                display: 'flex',
                alignItems: 'center',
            },
        };

        // Custom validation of filters.  We are primarily concerned with date.
        // Ensure it's valid.
        opts.validateParametersFunction = validateParameters;

        return opts;
    };

    getHistoryGridOptions = async (isMobile) => {
        const crewLead = crewLeadLabel(this.context.tenant?.tenantSettings);

        const hasJobAccess = this.context.checkUserHasPermission(ApplicationPermissions.job_create)
            || this.context.checkUserHasPermission(ApplicationPermissions.job_edit)
            || this.context.checkUserHasPermission(ApplicationPermissions.job_view);
        // for now default to false
        isMobile = false;

        const [dispatchLocations, jobTypes] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.jobTypes()),
        ]);

        // Admins can select all dispatch, others default to their dispatch loc.
        let initialDispatchValue = null;
        if (!this.context.user.isAdmin) initialDispatchValue = this.context.user.dispatchCompanyId;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
            disableFilter: false,
            initialFilterValue: initialDispatchValue,
        };

        const jobTypeFilterParams = {
            suppressFilterButton: true,
            options: jobTypes,
            optionsLabel: 'label',
            optionsValue: 'label',
            disableFilter: false,
            initialFilterValue: '',
        };

        const context = this;
        const opts = {
            domLayout: 'normal',
            components: {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                linkRenderer: LinkCellRenderer,
                buttonRenderer: ButtonCellRenderer,
                nameRenderer: LinkCellRenderer,
            },
            suppressMenuHide: true,
            headerHeight: isMobile ? 0 : 34,
            rowHeight: isMobile ? 76 : 40,
            suppressPropertyNamesCheck: true,
            rowModelType: 'infinite',
            cacheBlockSize: 18, // with including subcontractors, row height increases. less rows are visible
            defaultColDef: {
                sortable: !isMobile,
                resizable: !isMobile,
                suppressMenu: true,
                floatingFilter: !isMobile,
            },
            filter: !isMobile,
            unSortIcon: true,
            rowSelection: 'single',
            multiSortKey: 'ctrl',
            onSelectionChanged: (event) => {
                const selection = event.api.getSelectedRows();
                context.setState({
                    selectedRow: selection.length ? selection[0] : null,
                });
            },
            refresh: () => {
                context.state.gridApi.setDatasource(
                    context.state.historyDataSource,
                );
            },
            setStatus: (status) => {
                context.setState({ gridStatus: status });
            },
            columnDefs: [
                {
                    flex: 0,
                    width: 70,
                    minWidth: 70,
                    maxWidth: 70,
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    cellRenderer: indexCellRenderer,
                },
                {
                    colId: 'Customer',
                    minWidth: 220,
                    flex: 1,
                    headerName: 'Customer',
                    field: 'customer',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: (params) => {
                        if (!params?.node?.data) return null;

                        if (params?.node?.data?.subcontractor) {
                            return (
                                <span>
                                    {params.node.data.customer}
                                    <br />
                                    <small className="text-muted">
                                        {params.node.data.subcontractor}
                                    </small>
                                </span>
                            );
                        }

                        return params.node.data.customer;
                    },
                },
                {
                    colId: 'CrewLead',
                    flex: 1,
                    minWidth: 220,
                    headerName: crewLead,
                    headerClass: 'jobview-crewlead',
                    field: 'crewLead',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: (params) => {
                        if (!params?.node?.data) return null;

                        if (params?.node?.data?.crewLead) return params.node.data.crewLead;
                        return <span> - </span>;
                    },
                },
                {
                    colId: 'Foreman',
                    flex: 1,
                    minWidth: 220,
                    headerName: 'Customer Foreman',
                    field: 'foreman',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'DispatchCompanyId',
                    headerName: 'Dispatching',
                    sortable: true,
                    minWidth: 220,
                    flex: 1,
                    field: 'dispatchLocation',
                    tooltipField: 'dispatchLocation',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...dispatchFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...dispatchFilterParams },
                },
                {
                    colId: 'JobNumber',
                    minWidth: 180,
                    flex: 1,
                    headerName: 'Job Number',
                    field: 'jobNumber',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: hasJobAccess
                        ? (params) => {
                            if (!params.data) return null;

                            const { projectId, jobId, jobNumber } = params.data;

                            const to = projectId
                                ? `${AppNavPaths.Project}/${projectId}?openJob=${jobId}`
                                : `${AppNavPaths.Job}/${jobId}`;

                            return (
                                <Link
                                    className="site-link"
                                    to={to}
                                >
                                    {jobNumber}
                                </Link>
                            );
                        }
                        : null,
                },
                {
                    colId: 'JobLocation',
                    minWidth: 220,
                    flex: 1,
                    headerName: 'Job Location',
                    field: 'jobLocation',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: LinkCellRenderer,
                    cellRendererParams: {
                        href: getMapUrl,
                        nameField: 'jobLocation',
                        idField: 'jobLocationAddress',
                        title: 'View this Job Location',
                    },
                },
                {
                    colId: 'WorkOrderNumber',
                    minWidth: 150,
                    flex: 1,
                    headerName: 'WO Number',
                    field: 'workOrderNumber',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    width: 220,
                    minWidth: 220,
                    colId: 'AssignmentDate',
                    headerName: 'Date',
                    field: 'assignmentDate',
                    sortable: true,
                    filter: 'agDateColumnFilter',
                    filterParams: {
                        ...DateFilterDefaults,
                        inRangeInclusive: true,
                        browserDatePicker: true,
                        suppressFilterButton: true,
                    },
                    floatingFilter: true,
                    floatingFilterComponent:
                        DateRangeReadOnlyFloatingFilterComponent,
                    floatingFilterComponentParams: {},
                },
                {
                    width: 100,
                    minWidth: 100,
                    colId: 'StartTime',
                    headerName: 'Start',
                    headerClass: 'text-center',
                    field: 'startTime',
                    sortable: true,
                },
                {
                    headerName: 'Required',
                    width: 120,
                    minWidth: 120,
                    field: 'requiredResources',
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                },
                {
                    headerName: 'Assignments',
                    width: 120,
                    minWidth: 120,
                    field: 'assignmentCount',
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                },
                {
                    headerName: 'Unfilled',
                    width: 120,
                    minWidth: 120,
                    field: 'unfilledCount',
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                },
                {
                    colId: 'JobType',
                    minWidth: 180,
                    flex: 1,
                    headerName: 'Job Type',
                    field: 'jobType',
                    sortable: true,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...jobTypeFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...jobTypeFilterParams },
                },
                {
                    colId: 'HasGSF',
                    headerName: 'GSF',
                    width: 120,
                    minWidth: 120,
                    field: 'hasGSF',
                    sortable: true,
                    cellStyle: { textAlign: 'center' },
                    headerClass: 'text-center',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...gsfFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...gsfFilterParams },
                },
                {
                    width: 70,
                    minWidth: 70,
                    headerName: '',
                    headerClass: 'text-center',
                    headerComponent: IconHeader,
                    headerComponentParams: {
                        icon: faClock,
                    },
                    field: 'completedTimesheets',
                    sortable: false,
                    cellRenderer: (params) => {
                        if (!params.data) return '';
                        const completed = params.data?.completedTimesheets ?? 0;
                        const count = params.data?.timesheetsCount ?? 0;
                        const done = completed > 0 && completed === count;

                        const display = count > 0 ? completed / count : 0;

                        return (
                            <div className="w-100 text-right">
                                <Badge
                                    color={done ? 'success' : 'secondary'}
                                    style={{ fontSize: '1em' }}
                                >
                                    {display}
                                </Badge>
                            </div>
                        );
                    },
                    cellStyle: { textAlign: 'center' },
                },
            ],
        };

        opts.onGridReady = (params) => {
            // default date filter values
            const today = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');
            const dateRangeFilterComponent = params.api.getFilterInstance('AssignmentDate');
            dateRangeFilterComponent.setModel({
                filterType: 'date',
                type: 'inRange',
                dateFrom: today,
                dateTo: today,
            });

            params.api.onFilterChanged();
            opts.api = params.api;
            params.api.setDatasource(context.state.historyDataSource, opts);
        };

        opts.setLoading = (b) => {
            context.setState({ loadingData: b });
        };

        opts.showLoadingOverlay = false;
        opts.overlayNoRowsTemplate = 'No job information found for the selected filters';

        // debug only
        // opts.successCallback = (data) => context.setState({ rowData: data });

        if (isMobile) {
            opts.isFullWidthCell = () => true;
            opts.fullWidthCellRenderer = context.fullWidthCellRenderer;
        } else {
            opts.isFullWidthCell = () => false;
            opts.fullWidthCellRenderer = null;
        }

        opts.defaultColDef = {
            ...opts.defaultColDef,
            cellStyle: {
                lineHeight: '18px',
                display: 'flex',
                alignItems: 'center',
            },
        };

        // Custom validation of filters.  We are primarily concerned with date.
        // Ensure it's valid.
        opts.validateParametersFunction = validateHistoryParameters;

        return opts;
    };

    setupGrid = async (isMobile) => {
        const gridOptions = await this.getGridOptions(isMobile);

        const dataSource = createDataSource(
            ApiRoutes.job.dailyView(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions: { ...gridOptions },
            dataSource: { ...dataSource },
            isMobile,
            currentWidth: window.innerWidth,
        });
    };

    setupHistoryGrid = async (isMobile) => {
        const historyGridOptions = await this.getHistoryGridOptions(isMobile);

        const historyDataSource = createDataSource(
            ApiRoutes.job.dailyViewHistory(),
            historyGridOptions,
        );

        this.setState({
            loading: false,
            historyGridOptions,
            historyDataSource,
            isMobile,
            currentWidth: window.innerWidth,
        });
    };

    fullWidthCellRenderer = (params) => {
        const view = params?.node?.data;

        const crewLead = crewLeadLabel(this.context.tenant?.tenantSettings);

        return renderToString(
            <FlexStartRow className="p-2">
                <FlexColumnStart>
                    <strong>{view?.customer}</strong>
                    <span>
                        <strong className="text-muted mr-2">Foreman</strong>
                        {view?.foreman}
                    </span>
                    <span>
                        <strong className="text-muted mr-2">{crewLead}</strong>
                        {view?.crewLead}
                    </span>
                </FlexColumnStart>
                <FlexColumnStart />
            </FlexStartRow>,
        );
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const isMobile = window.innerWidth <= 767;
            const { user } = this.context;
            this.setState(
                { user },
                async () => {
                    await this.setupGrid(isMobile);
                    await this.setupHistoryGrid(isMobile);
                },
            );
        }
    }

    toggleTab = (tab) => {
        const { activeTab } = this.state;

        if (activeTab !== tab) {
            this.setState({
                activeTab: tab,
            });
        }
    };

    render() {
        const {
            dataSource,
            rowData,
            isHistoryExporting,
            gridOptions,
            loading,
            loadingData,
            historyDataSource,
            historyRowData,
            historyGridOptions,
            activeTab,
            saving,
            dispatchLocations,
        } = { ...this.state };

        if (loading || !gridOptions) {
            return <Progress />;
        }

        return (
            <PageWrap>
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            className={classnames({
                                active: activeTab === '1',
                            })}
                            onClick={() => {
                                this.toggleTab('1');
                            }}
                        >
                            <>
                                <FontAwesomeIcon
                                    icon={faCalendarDay}
                                    className="mr-2 text-muted"
                                />
                                <span className="mr-2">Daily Jobs View</span>
                                {!!loadingData && (
                                    <span className="fa fa-spin fa-circle-notch text-danger" />
                                )}
                            </>
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            className={classnames({
                                active: activeTab === '2',
                            })}
                            onClick={() => {
                                this.toggleTab('2');
                            }}
                        >
                            <>
                                <FontAwesomeIcon
                                    icon={faMapMarkedAlt}
                                    className="mr-2 text-muted"
                                />

                                <span className="mr-2">Map View</span>
                            </>
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            className={classnames({
                                active: activeTab === '3',
                            })}
                            onClick={() => {
                                this.toggleTab('3');
                            }}
                        >
                            <>
                                <FontAwesomeIcon
                                    icon={faHistory}
                                    className="mr-2 text-muted"
                                />
                                <span className="mr-2">Job History</span>
                                {!!loadingData && (
                                    <span className="fa fa-spin fa-circle-notch text-danger" />
                                )}
                            </>
                        </NavLink>
                    </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                    <TabPane tabId="1" />
                </TabContent>
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="2" />
                </TabContent>
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="3" />
                </TabContent>

                {activeTab === '1' && (
                    <>
                        <DataGridToolbar
                            mobileTitle="Daily Jobs View"
                            hideAdd
                            hideExcelButton
                            gridApi={gridOptions.api}
                            dataSource={dataSource}
                        >
                            <SmallButton
                                disabled={Boolean(saving)}
                                onClick={this.onExportToExcelClicked}
                            >
                                <i className="fa fa-file-excel fa-lg" />
                                <span className="ml-2 small-viewport-hide">
                                    Export to Excel
                                </span>
                            </SmallButton>
                        </DataGridToolbar>
                        <DataGrid
                            domLayout="normal"
                            rowData={rowData}
                            gridOptions={gridOptions}
                        />
                    </>
                )}

                {activeTab === '2' && (
                    <JobMapView dispatchLocations={dispatchLocations} />
                )}

                {activeTab === '3' && (
                    <>
                        <DataGridToolbar
                            mobileTitle="Jobs History"
                            hideAdd
                            hideExcelButton
                            gridApi={historyGridOptions.api}
                            dataSource={historyDataSource}
                        >
                            <SmallButton
                                disabled={Boolean(saving)}
                                onClick={this.onExportHistoryToExcelClicked}
                            >
                                <i className="fa fa-file-excel fa-lg" />
                                <span className="ml-2 small-viewport-hide">
                                    Export to Excel
                                </span>
                            </SmallButton>
                        </DataGridToolbar>
                        <DataGrid
                            domLayout="normal"
                            rowData={historyRowData}
                            gridOptions={historyGridOptions}
                        />
                    </>
                )}

                <Alert
                    isOpen={isHistoryExporting}
                    title="Job History Export"
                >
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Exporting...
                </Alert>
            </PageWrap>
        );
    }
}
export default DailyJobView;